import React, { ReactNode, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';

import './i18n';
import i18n from './i18n';

import { AuthProvider, useAuth } from './context/AuthContext';
import { AuditFocusProvider } from './context/AuditFocusContext';
import { ThemeProvider } from './context/ThemeContext';
import { DensityProvider } from './context/DensityContext';
import Navigation from './components/Navigation';
import { Loader } from 'lucide-react';
import { getResolvedNavFeatures } from './utils/navFeatures';
import {
  ConfirmProvider,
  SkipLink,
  Toaster,
  CommandPaletteProvider,
  KeyboardShortcuts,
  Footer,
} from './components/ui';

// Route-level code splitting (U46). Each page is a separate async chunk so
// the initial bundle only ships the shell + auth. Named webpack chunks let
// devtools / source-map-explorer show meaningful file names.
// eslint-disable-next-line @typescript-eslint/naming-convention
const Login = lazy(() => import(/* webpackChunkName: "auth" */ './pages/Login'));
const Register = lazy(() => import(/* webpackChunkName: "auth" */ './pages/Register'));

const Dashboard = lazy(() => import(/* webpackChunkName: "dashboard" */ './pages/Dashboard'));

const Policies = lazy(() => import(/* webpackChunkName: "policies" */ './pages/Policies'));
const PolicyDetail = lazy(() => import(/* webpackChunkName: "policies" */ './pages/PolicyDetail'));
const PolicyCalendar = lazy(() => import(/* webpackChunkName: "policies" */ './pages/PolicyCalendar'));
const PolicyWizard = lazy(() => import(/* webpackChunkName: "policy-wizard" */ './pages/PolicyWizard'));
const PolicyTemplateLibrary = lazy(
  () => import(/* webpackChunkName: "policy-templates" */ './pages/PolicyTemplateLibrary')
);
const Frameworks = lazy(() => import(/* webpackChunkName: "frameworks" */ './pages/Frameworks'));
const CompanyProfile = lazy(() => import(/* webpackChunkName: "company-profile" */ './pages/CompanyProfile'));
const PolicyAiDraft = lazy(() => import(/* webpackChunkName: "policy-ai-draft" */ './pages/PolicyAiDraft'));
const ContinuousEvidence = lazy(() => import(/* webpackChunkName: "continuous-evidence" */ './pages/ContinuousEvidence'));
const ReadinessWalkthrough = lazy(() => import(/* webpackChunkName: "readiness" */ './pages/ReadinessWalkthrough'));

const Activities = lazy(() => import(/* webpackChunkName: "activities" */ './pages/Activities'));

const Audit = lazy(() => import(/* webpackChunkName: "audit" */ './pages/Audit'));
const AuditList = lazy(() => import(/* webpackChunkName: "audit" */ './pages/AuditList'));
const QuestionnaireSystem = lazy(() => import(/* webpackChunkName: "questionnaire" */ './components/QuestionnaireSystem'));
const AuditProgramManagement = lazy(() => import(/* webpackChunkName: "audit-programs" */ './components/AuditProgramManagement'));
const PerformanceDashboard = lazy(() => import(/* webpackChunkName: "performance" */ './components/PerformanceDashboard'));

const AdminDashboard = lazy(() => import(/* webpackChunkName: "admin" */ './pages/AdminDashboard'));
const AdminCustomerDetail = lazy(() => import(/* webpackChunkName: "admin" */ './pages/AdminCustomerDetail'));
const AdminCreateCustomer = lazy(() => import(/* webpackChunkName: "admin" */ './pages/AdminCreateCustomer'));
const TeamManagement = lazy(() => import(/* webpackChunkName: "team" */ './pages/TeamManagement'));
const ProfileLayout = lazy(() => import(/* webpackChunkName: "profile" */ './pages/ProfileLayout'));
const Acknowledge = lazy(() => import(/* webpackChunkName: "acknowledge" */ './pages/Acknowledge'));
const RiskRegister = lazy(() => import(/* webpackChunkName: "risks" */ './pages/RiskRegister'));
const VendorManagement = lazy(() => import(/* webpackChunkName: "vendors" */ './pages/VendorManagement'));
const ProfileSecurity = lazy(() => import(/* webpackChunkName: "profile" */ './pages/ProfileSecurity'));
const ProfileSSO = lazy(() => import(/* webpackChunkName: "profile" */ './pages/ProfileSSO'));
const TrustCenter = lazy(() => import(/* webpackChunkName: "trust" */ './pages/TrustCenter'));
const SsoCallback = lazy(() => import(/* webpackChunkName: "auth" */ './pages/SsoCallback'));
const EmployeeCompliance = lazy(() => import(/* webpackChunkName: "employees" */ './pages/EmployeeCompliance'));
const ComplianceReport = lazy(() => import(/* webpackChunkName: "reports" */ './pages/ComplianceReport'));
const Billing = lazy(() => import(/* webpackChunkName: "billing" */ './pages/Billing'));

const APP_VERSION =
  (process.env.REACT_APP_VERSION as string | undefined) ||
  (process.env.npm_package_version as string | undefined) ||
  '0.1.0';
const APP_BUILD = process.env.REACT_APP_BUILD_SHA as string | undefined;

const LoadingSpinner = () => (
  <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900/60 dark:bg-neutral-950 flex items-center justify-center">
    <div className="text-center">
      <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-brand-600" />
      <p className="text-neutral-600 dark:text-neutral-400">Loading…</p>
    </div>
  </div>
);

const PrivateRoute = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const PublicRoute = ({
  children,
  redirectWhenAuthenticated = true,
}: {
  children: ReactNode;
  /** When false (e.g. `/login`), still render children so users can sign out and switch accounts. */
  redirectWhenAuthenticated?: boolean;
}) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (redirectWhenAuthenticated && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

const SuperadminRoute = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, isSuperadmin, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!isSuperadmin) return <Navigate to="/dashboard" />;
  return <>{children}</>;
};

/** Team tab under /profile: send non-managers to company tab instead of dashboard. */
const ProfileTeamRoute = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, canManageTeam, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!canManageTeam) return <Navigate to="/profile/company" replace />;
  return <>{children}</>;
};

/** External auditor / guest portal: only dashboard + policies (nav); block direct URLs to other areas. */
const ReadOnlyPortalRoute = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, isExternalAuditor, isGuest, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (isExternalAuditor || isGuest) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

/** External auditor / guest accounts cannot use internal workflows (questionnaire, activity tracking, policy wizard). */
const NonAuditorRoute = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, isExternalAuditor, isGuest, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (isExternalAuditor || isGuest) return <Navigate to="/policies" replace />;
  return <>{children}</>;
};

/** Blocks route when the active company does not have this area enabled (super admin configures per customer). */
const FeatureRoute = ({
  feature,
  children
}: {
  feature: 'audit' | 'programs' | 'performance';
  children: ReactNode;
}) => {
  const { loading, company } = useAuth();
  if (loading) return <LoadingSpinner />;
  const nav = getResolvedNavFeatures(company);
  if (!nav[feature]) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

const SmartRedirect = () => {
  const { isAuthenticated, isSuperadmin, activeCompanyId, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (isSuperadmin && !activeCompanyId) return <Navigate to="/admin" />;
  return <Navigate to="/dashboard" />;
};

const AppRoutes = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Router>
      <AuditFocusProvider>
      <CommandPaletteProvider>
        <KeyboardShortcuts />
        <SkipLink />
        {isAuthenticated && <Navigation />}
        <div className="flex min-h-screen flex-col">
        <main id="main" tabIndex={-1} className="outline-none flex-1">
        <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/acknowledge/:token" element={<Acknowledge />} />
          <Route path="/trust/:companyId" element={<TrustCenter />} />
          <Route path="/sso-callback" element={<SsoCallback />} />
          <Route path="/login" element={
            <PublicRoute redirectWhenAuthenticated={false}><Login /></PublicRoute>
          } />
          <Route path="/register" element={
            <PublicRoute><Register /></PublicRoute>
          } />

          <Route path="/admin" element={
            <SuperadminRoute><AdminDashboard /></SuperadminRoute>
          } />
          <Route path="/admin/customers/new" element={
            <SuperadminRoute><AdminCreateCustomer /></SuperadminRoute>
          } />
          <Route path="/admin/customers/:id" element={
            <SuperadminRoute><AdminCustomerDetail /></SuperadminRoute>
          } />

          <Route path="/team" element={<Navigate to="/profile/team" replace />} />
          <Route path="/settings/company" element={<Navigate to="/profile/company" replace />} />

          <Route path="/profile" element={
            <PrivateRoute>
              <NonAuditorRoute>
                <ProfileLayout />
              </NonAuditorRoute>
            </PrivateRoute>
          }>
            <Route index element={<Navigate to="company" replace />} />
            <Route path="company" element={<CompanyProfile />} />
            <Route path="team" element={
              <ProfileTeamRoute><TeamManagement /></ProfileTeamRoute>
            } />
            <Route path="security" element={<ProfileSecurity />} />
            <Route path="sso" element={<ProfileSSO />} />
          </Route>

          <Route path="/dashboard" element={
            <PrivateRoute><Dashboard /></PrivateRoute>
          } />
          <Route path="/audit" element={
            <PrivateRoute>
              <ReadOnlyPortalRoute>
                <FeatureRoute feature="audit"><AuditList /></FeatureRoute>
              </ReadOnlyPortalRoute>
            </PrivateRoute>
          } />
          <Route path="/audit/:auditId/controls" element={
            <PrivateRoute>
              <ReadOnlyPortalRoute>
                <FeatureRoute feature="audit"><Audit /></FeatureRoute>
              </ReadOnlyPortalRoute>
            </PrivateRoute>
          } />
          <Route path="/questionnaire" element={
            <PrivateRoute><NonAuditorRoute><QuestionnaireSystem /></NonAuditorRoute></PrivateRoute>
          } />
          <Route path="/audit-programs" element={
            <PrivateRoute>
              <ReadOnlyPortalRoute>
                <FeatureRoute feature="programs"><AuditProgramManagement /></FeatureRoute>
              </ReadOnlyPortalRoute>
            </PrivateRoute>
          } />
          <Route path="/performance" element={
            <PrivateRoute>
              <ReadOnlyPortalRoute>
                <FeatureRoute feature="performance"><PerformanceDashboard /></FeatureRoute>
              </ReadOnlyPortalRoute>
            </PrivateRoute>
          } />
          <Route path="/policies" element={
            <PrivateRoute><Policies /></PrivateRoute>
          } />
          <Route path="/activities" element={
            <PrivateRoute><NonAuditorRoute><Activities /></NonAuditorRoute></PrivateRoute>
          } />
          <Route path="/policies/wizard" element={
            <PrivateRoute><NonAuditorRoute><PolicyWizard /></NonAuditorRoute></PrivateRoute>
          } />
          <Route path="/policies/templates" element={
            <PrivateRoute><NonAuditorRoute><PolicyTemplateLibrary /></NonAuditorRoute></PrivateRoute>
          } />
          <Route path="/policies/ai-draft" element={
            <PrivateRoute><NonAuditorRoute><PolicyAiDraft /></NonAuditorRoute></PrivateRoute>
          } />
          <Route path="/policies/calendar" element={
            <PrivateRoute><NonAuditorRoute><PolicyCalendar /></NonAuditorRoute></PrivateRoute>
          } />
          <Route path="/policies/:id" element={
            <PrivateRoute><PolicyDetail /></PrivateRoute>
          } />
          <Route path="/frameworks" element={
            <PrivateRoute><NonAuditorRoute><Frameworks /></NonAuditorRoute></PrivateRoute>
          } />
          <Route path="/evidence" element={
            <PrivateRoute><NonAuditorRoute><ContinuousEvidence /></NonAuditorRoute></PrivateRoute>
          } />
          <Route path="/readiness" element={
            <PrivateRoute><NonAuditorRoute><ReadinessWalkthrough /></NonAuditorRoute></PrivateRoute>
          } />
          <Route path="/readiness/:controlId" element={
            <PrivateRoute><NonAuditorRoute><ReadinessWalkthrough /></NonAuditorRoute></PrivateRoute>
          } />
          <Route path="/risks" element={
            <PrivateRoute><NonAuditorRoute><RiskRegister /></NonAuditorRoute></PrivateRoute>
          } />
          <Route path="/vendors" element={
            <PrivateRoute><NonAuditorRoute><VendorManagement /></NonAuditorRoute></PrivateRoute>
          } />
          <Route path="/employees" element={
            <PrivateRoute><NonAuditorRoute><EmployeeCompliance /></NonAuditorRoute></PrivateRoute>
          } />
          <Route path="/reports" element={
            <PrivateRoute><NonAuditorRoute><ComplianceReport /></NonAuditorRoute></PrivateRoute>
          } />
          <Route path="/billing" element={
            <PrivateRoute><NonAuditorRoute><Billing /></NonAuditorRoute></PrivateRoute>
          } />
          <Route path="/" element={<SmartRedirect />} />
        </Routes>
        </Suspense>
        </main>
        {isAuthenticated && (
          <Footer
            className="print-hide"
            version={APP_VERSION}
            build={APP_BUILD}
          />
        )}
        </div>
      </CommandPaletteProvider>
      </AuditFocusProvider>
    </Router>
  );
};

function App() {
  return (
    <HelmetProvider>
      <I18nextProvider i18n={i18n}>
        <ThemeProvider>
          <DensityProvider>
            <AuthProvider>
              <ConfirmProvider>
                <div className="min-h-screen overflow-x-hidden bg-neutral-50 dark:bg-neutral-900/60 text-neutral-900 dark:bg-neutral-950 dark:text-neutral-100 transition-colors">
                  <AppRoutes />
                  <Toaster />
                </div>
              </ConfirmProvider>
            </AuthProvider>
          </DensityProvider>
        </ThemeProvider>
      </I18nextProvider>
    </HelmetProvider>
  );
}

export default App;
